import { useMutation, useQuery, useQueryClient } from 'react-query';
import { packageApi } from './api';
import type {
  PackageFilters,
  PackageFormValues,
  CreatePackagePayload,
  UpdatePackagePayload,
} from './types';

const PACKAGE_LIST_KEY = 'packages:list';
const PACKAGE_DETAIL_KEY = 'packages:detail';
const FUNCTIONS_KEY = 'packages:functions';

export const usePackageList = (filters: PackageFilters = {}) => {
  return useQuery([PACKAGE_LIST_KEY, filters], () => packageApi.list(filters), {
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });
};

export const usePackageDetail = (id?: string, enabled = true) => {
  return useQuery([PACKAGE_DETAIL_KEY, id], () => packageApi.get(id!), {
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFunctions = () => {
  return useQuery([FUNCTIONS_KEY], () => packageApi.getFunctions(), {
    staleTime: 10 * 60 * 1000,
  });
};

export const useCreatePackage = () => {
  const queryClient = useQueryClient();
  return useMutation((payload: CreatePackagePayload) => packageApi.create(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PACKAGE_LIST_KEY] });
    },
  });
};

export const useUpdatePackage = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload }: { id: string; payload: UpdatePackagePayload }) => packageApi.update(id, payload),
    {
      onSuccess: (record) => {
        queryClient.invalidateQueries({ queryKey: [PACKAGE_LIST_KEY] });
        queryClient.setQueryData([PACKAGE_DETAIL_KEY, record.id], record);
      },
    }
  );
};

export const useTogglePackageStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      status === 'active' ? packageApi.activate(id) : packageApi.deactivate(id),
    {
      onSuccess: (record) => {
        queryClient.invalidateQueries({ queryKey: [PACKAGE_LIST_KEY] });
        queryClient.setQueryData([PACKAGE_DETAIL_KEY, record.id], record);
      },
    }
  );
};

export const useDeletePackage = () => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => packageApi.delete(id), {
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [PACKAGE_LIST_KEY] });
      queryClient.removeQueries([PACKAGE_DETAIL_KEY, id]);
    },
  });
};

export type PackageFormSubmitHandler = (values: PackageFormValues) => void;


